import React, { useEffect, useMemo, useState } from "react";
import {
  Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  Legend, Line, Pie, PieChart, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ArrowDownRight, ArrowRight, ArrowUpRight, Banknote, BookOpen,
  Building2, CalendarRange, Check, CircleAlert, Database, Landmark,
  Mail, RefreshCw, Settings2, ShieldCheck, Sparkles, WalletCards,
} from "lucide-react";
import { getBusinessOverview } from "@/lib/api";

const money = (value, compact = false) => {
  const amount = Number(value || 0);
  if (compact) {
    if (Math.abs(amount) >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (Math.abs(amount) >= 1000) return `₹${(amount / 1000).toFixed(0)}k`;
  }
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount);
};

const chartMoney = value => {
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(0)}L`;
  return `₹${Math.round(value / 1000)}k`;
};

const tooltipStyle = {
  background: "#111827", border: "1px solid rgba(255,255,255,.12)",
  borderRadius: 10, color: "#fff", fontSize: 11,
};

const COLORS = ["#22d3ee", "#8b5cf6", "#f59e0b", "#10b981", "#f43f5e", "#3b82f6"];

function previewData(fiscalYearStart) {
  const actual = [
    ["Apr", 4200000, 3200000, 12400000],
    ["May", 4800000, 3500000, 13700000],
    ["Jun", 4100000, 3800000, 14000000],
    ["Jul", 5700000, 3900000, 15800000],
    ["Aug", 5400000, 4100000, 17100000],
  ];
  return {
    mode: "preview",
    fiscal_year_start: fiscalYearStart,
    fiscal_year: `FY ${String(fiscalYearStart).slice(-2)}–${String(fiscalYearStart + 1).slice(-2)}`,
    summary: {
      cash_on_hand: 17100000, revenue: 24200000, operating_profit: 5700000,
      operating_margin_pct: 23.6, average_monthly_cost: 3700000, runway_months: 4.6,
      records: 418, active_months: 5,
    },
    monthly: actual.map(([label, revenue, expenses, closing_cash], index) => ({
      period: `${fiscalYearStart}-${String(index + 4).padStart(2, "0")}`,
      label, revenue, expenses, net_cash_flow: revenue - expenses,
      closing_cash, transaction_count: 70 + index * 7,
    })),
    expense_breakdown: [
      { name: "People & partners", amount: 7400000 },
      { name: "Materials & fulfilment", amount: 4550000 },
      { name: "Marketing", amount: 2680000 },
      { name: "Rent & operations", amount: 2190000 },
      { name: "Software & services", amount: 980000 },
      { name: "Other", amount: 700000 },
    ],
    customer_breakdown: [
      { name: "Enterprise clients", amount: 10100000 },
      { name: "Retail & D2C", amount: 7200000 },
      { name: "Projects", amount: 4800000 },
      { name: "Other revenue", amount: 2100000 },
    ],
    connections: {
      zoho: { connected: false, label: "Zoho Books" },
      gmail: { connected: true, label: "Gmail statements" },
      ledger: { connected: true, label: "Approved ledger" },
    },
  };
}

function buildProjection(data, assumptions) {
  const monthly = data.monthly || [];
  const lastActiveIndex = monthly.reduce((last, item, index) => item.transaction_count > 0 ? index : last, -1);
  const actual = lastActiveIndex >= 0 ? monthly.slice(0, lastActiveIndex + 1) : [];
  const base = actual.filter(item => item.transaction_count > 0).slice(-3);
  if (!base.length) return [];
  const averageRevenue = base.reduce((sum, item) => sum + Number(item.revenue || 0), 0) / base.length;
  const averageExpenses = base.reduce((sum, item) => sum + Number(item.expenses || 0), 0) / base.length;
  const allLabels = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
  const lastActual = actual[actual.length - 1];
  let cash = Number(
    data.summary?.cash_is_estimated
      ? (lastActual.closing_cash || data.summary?.cash_on_hand || 0)
      : (data.summary?.cash_on_hand || lastActual.closing_cash || 0)
  );
  const actualRows = actual.map(item => ({
    ...item, actualCash: Number(item.closing_cash || 0), forecastCash: null,
  }));
  if (!data.summary?.cash_is_estimated && actualRows.length) {
    actualRows[actualRows.length - 1].actualCash = Number(data.summary.cash_on_hand || 0);
  }
  const remainingLabels = allLabels.slice(lastActiveIndex + 1);
  const projected = remainingLabels.map((label, index) => {
    const growth = Math.pow(1 + assumptions.revenueGrowth / 100, (index + 1) / 12);
    const costGrowth = Math.pow(1 + assumptions.costGrowth / 100, (index + 1) / 12);
    const collectionFactor = Math.max(.72, 1 - assumptions.collectionDays / 210);
    const revenue = averageRevenue * growth * collectionFactor;
    const expenses = averageExpenses * costGrowth;
    cash += revenue - expenses;
    return {
      label, revenue, expenses, net_cash_flow: revenue - expenses,
      actualCash: null, forecastCash: cash,
    };
  });
  if (projected.length && actualRows.length) {
    actualRows[actualRows.length - 1].forecastCash = actualRows[actualRows.length - 1].actualCash;
  }
  return [...actualRows, ...projected];
}

function MetricCard({ label, value, detail, tone = "cyan", icon: Icon, trend }) {
  const tones = {
    cyan: "text-cyan-300 bg-cyan-400/10 border-cyan-400/20",
    green: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
    violet: "text-violet-300 bg-violet-400/10 border-violet-400/20",
    amber: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  };
  return <div className="rounded-xl border border-white/10 bg-[var(--bg-surface)] p-4 min-w-0">
    <div className="flex items-start justify-between gap-3">
      <div className="text-[10px] uppercase tracking-[.16em] text-[var(--text-muted)]">{label}</div>
      <div className={`p-2 rounded-lg border ${tones[tone]}`}><Icon size={15}/></div>
    </div>
    <div className="font-mono text-2xl md:text-[1.65rem] font-semibold tracking-tight mt-3 truncate">{value}</div>
    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-[var(--text-muted)]">
      {trend === "up" && <ArrowUpRight size={12} className="text-emerald-300"/>}
      {trend === "down" && <ArrowDownRight size={12} className="text-rose-300"/>}
      <span>{detail}</span>
    </div>
  </div>;
}

function Panel({ title, subtitle, action, children, className = "" }) {
  return <section className={`rounded-xl border border-white/10 bg-[var(--bg-surface)] p-4 md:p-5 ${className}`}>
    <div className="flex items-start justify-between gap-4 mb-4">
      <div><h2 className="text-sm font-semibold">{title}</h2>{subtitle && <p className="text-[10px] text-[var(--text-muted)] mt-1">{subtitle}</p>}</div>
      {action}
    </div>
    {children}
  </section>;
}

function SourceRow({ icon: Icon, name, detail, connected }) {
  return <div className="flex items-center gap-3 py-3 border-b border-white/[.06] last:border-0">
    <div className="h-9 w-9 rounded-lg bg-white/[.04] border border-white/[.07] grid place-items-center text-slate-300"><Icon size={16}/></div>
    <div className="flex-1 min-w-0"><div className="text-xs font-medium">{name}</div><div className="text-[9px] text-[var(--text-muted)] mt-1 truncate">{detail}</div></div>
    <span className={`text-[9px] px-2 py-1 rounded-full border ${connected ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-300" : "border-white/10 text-slate-400"}`}>{connected ? "Connected" : "Not connected"}</span>
  </div>;
}

export default function BusinessDashboardPage({ onNavigate }) {
  const now = new Date();
  const currentFy = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const [fiscalYear, setFiscalYear] = useState(currentFy);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assumptions, setAssumptions] = useState({ revenueGrowth: 12, costGrowth: 7, collectionDays: 18 });

  const load = async () => {
    setLoading(true);
    try {
      const response = await getBusinessOverview(fiscalYear);
      setData(response.data?.mode === "empty" ? previewData(fiscalYear) : response.data);
    } catch {
      setData(previewData(fiscalYear));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [fiscalYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const projection = useMemo(() => data ? buildProjection(data, assumptions) : [], [data, assumptions]);
  const actual = (data?.monthly || []).filter(item => item.transaction_count > 0);
  const lastActual = actual[actual.length - 1];
  const forecastEnd = projection[projection.length - 1]?.forecastCash || lastActual?.closing_cash || 0;
  const forecastRevenue = projection.filter(item => item.forecastCash !== null && item.actualCash === null).reduce((sum, item) => sum + item.revenue, 0);
  const expenseTotal = (data?.expense_breakdown || []).reduce((sum, item) => sum + item.amount, 0);
  const biggestCost = data?.expense_breakdown?.[0];

  if (!data) return <div className="p-6 text-sm text-[var(--text-muted)]">Preparing the business cockpit…</div>;

  return <div className="p-4 md:p-6 xl:p-8 space-y-4 md:space-y-5 max-w-[1600px] mx-auto" data-testid="business-dashboard">
    <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[.2em] text-cyan-300 font-semibold"><Building2 size={13}/> Capital360 Business</div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-2">Operating cockpit</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">One visual layer for cash, revenue, costs and the decisions ahead—grounded in approved Zoho and bank-statement data.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-[var(--bg-surface)] px-3 text-xs text-slate-300"><CalendarRange size={14}/><select value={fiscalYear} onChange={event => setFiscalYear(Number(event.target.value))} className="bg-transparent outline-none text-white"><option value={currentFy}>{`FY ${String(currentFy).slice(-2)}–${String(currentFy + 1).slice(-2)}`}</option><option value={currentFy - 1}>{`FY ${String(currentFy - 1).slice(-2)}–${String(currentFy).slice(-2)}`}</option></select></label>
        <button onClick={load} disabled={loading} className="h-9 inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 text-xs hover:bg-white/5"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/>Refresh</button>
        <button onClick={() => onNavigate("accounts")} className="h-9 inline-flex items-center gap-2 rounded-lg bg-cyan-400 text-slate-950 px-3 text-xs font-semibold hover:bg-cyan-300"><Settings2 size={14}/>Connections</button>
      </div>
    </header>

    {data.mode === "preview" && <div className="rounded-xl border border-amber-400/20 bg-amber-400/[.07] p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="business-preview-banner">
      <CircleAlert size={17} className="text-amber-300 shrink-0"/>
      <div className="flex-1"><div className="text-xs font-medium text-amber-100">Illustrative preview</div><div className="text-[10px] text-amber-100/60 mt-0.5">No approved business rows were found for this period. These sample numbers show how the live dashboard will behave.</div></div>
      <button onClick={() => onNavigate("statement_imports")} className="text-[10px] text-amber-100 inline-flex items-center gap-1">Review data sources <ArrowRight size={12}/></button>
    </div>}

    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
      <MetricCard label={data.summary.cash_is_estimated ? "Cash movement · YTD" : "Cash on hand"} value={money(data.summary.cash_on_hand, true)} detail={data.summary.cash_is_estimated ? "Estimated from approved operating flows" : `${data.summary.runway_months ?? "—"} months of operating runway`} icon={Landmark} tone="cyan"/>
      <MetricCard label="Revenue · YTD" value={money(data.summary.revenue, true)} detail={`${actual.length} months recognised`} icon={Banknote} tone="green" trend="up"/>
      <MetricCard label="Operating profit" value={money(data.summary.operating_profit, true)} detail={`${data.summary.operating_margin_pct}% operating margin`} icon={WalletCards} tone="violet"/>
      <MetricCard label="FY closing cash" value={money(forecastEnd, true)} detail={`${money(forecastRevenue, true)} projected collections ahead`} icon={Sparkles} tone="amber" trend={forecastEnd >= data.summary.cash_on_hand ? "up" : "down"}/>
    </div>

    <div className="grid xl:grid-cols-[minmax(0,1.65fr)_minmax(310px,.75fr)] gap-4">
      <Panel title="Cash outlook" subtitle="Solid line is source-backed actuals; dotted line is your adjustable operating scenario">
        <ResponsiveContainer width="100%" height={330}>
          <ComposedChart data={projection} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs><linearGradient id="cashFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22d3ee" stopOpacity={.22}/><stop offset="100%" stopColor="#22d3ee" stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid stroke="rgba(255,255,255,.055)" vertical={false}/>
            <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false}/>
            <YAxis tickFormatter={chartMoney} tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} width={58}/>
            <Tooltip formatter={(value, name) => [money(value), name === "actualCash" ? "Actual cash" : "Forecast cash"]} contentStyle={tooltipStyle}/>
            <ReferenceLine y={0} stroke="rgba(255,255,255,.22)"/>
            <Area type="monotone" dataKey="actualCash" name="Actual cash" stroke="#22d3ee" strokeWidth={3} fill="url(#cashFill)" connectNulls={false}/>
            <Line type="monotone" dataKey="forecastCash" name="Forecast cash" stroke="#fbbf24" strokeWidth={2.5} strokeDasharray="6 5" dot={{ r: 3, fill: "#111827", stroke: "#fbbf24" }} connectNulls={false}/>
          </ComposedChart>
        </ResponsiveContainer>
        <div className="grid sm:grid-cols-3 gap-4 mt-2 pt-4 border-t border-white/[.07]">
          <ScenarioControl label="Revenue growth" value={assumptions.revenueGrowth} suffix="%" min={-20} max={40} onChange={value => setAssumptions({...assumptions, revenueGrowth: value})}/>
          <ScenarioControl label="Cost inflation" value={assumptions.costGrowth} suffix="%" min={0} max={25} onChange={value => setAssumptions({...assumptions, costGrowth: value})}/>
          <ScenarioControl label="Collection delay" value={assumptions.collectionDays} suffix=" days" min={0} max={60} onChange={value => setAssumptions({...assumptions, collectionDays: value})}/>
        </div>
      </Panel>

      <Panel title="Source health" subtitle="Accounting remains in Zoho; Capital360 stays read-only">
        <SourceRow icon={BookOpen} name="Zoho Books" detail="Invoices, bills and transaction ledger" connected={data.connections?.zoho?.connected}/>
        <SourceRow icon={Mail} name="Gmail statements" detail="Read-only bank statement attachments" connected={data.connections?.gmail?.connected}/>
        <SourceRow icon={Database} name="Approved ledger" detail={`${data.summary.records || 0} business movements in this period`} connected={data.connections?.ledger?.connected}/>
        <div className="mt-4 rounded-lg border border-cyan-400/15 bg-cyan-400/[.055] p-3 flex gap-3"><ShieldCheck size={16} className="text-cyan-300 shrink-0"/><div><div className="text-xs font-medium">No duplicate bookkeeping</div><div className="text-[10px] text-[var(--text-muted)] mt-1 leading-4">Zoho remains the source of truth. Bank feeds are used for reconciliation and cash visibility.</div></div></div>
      </Panel>
    </div>

    <div className="grid xl:grid-cols-2 gap-4">
      <Panel title="Revenue vs operating cost" subtitle="Monthly recognised revenue and business expenditure">
        <ResponsiveContainer width="100%" height={290}><BarChart data={projection} barGap={3}>
          <CartesianGrid stroke="rgba(255,255,255,.055)" vertical={false}/><XAxis dataKey="label" tick={{fill:"#94a3b8",fontSize:10}} axisLine={false} tickLine={false}/><YAxis tickFormatter={chartMoney} tick={{fill:"#64748b",fontSize:10}} axisLine={false} tickLine={false} width={58}/><Tooltip formatter={value => money(value)} contentStyle={tooltipStyle}/><Legend wrapperStyle={{fontSize:10}}/><Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[3,3,0,0]}/><Bar dataKey="expenses" name="Operating cost" fill="#8b5cf6" radius={[3,3,0,0]}/>
        </BarChart></ResponsiveContainer>
      </Panel>
      <Panel title="Where the money goes" subtitle="Business costs grouped from approved transactions">
        <div className="grid sm:grid-cols-[220px_minmax(0,1fr)] items-center gap-4">
          <ResponsiveContainer width="100%" height={230}><PieChart><Pie data={data.expense_breakdown || []} dataKey="amount" nameKey="name" innerRadius={62} outerRadius={88} paddingAngle={2}>{(data.expense_breakdown || []).map((entry,index)=><Cell key={entry.name} fill={COLORS[index%COLORS.length]}/>)}</Pie><Tooltip formatter={value=>money(value)} contentStyle={tooltipStyle}/></PieChart></ResponsiveContainer>
          <div className="space-y-3">{(data.expense_breakdown || []).slice(0,6).map((item,index)=><div key={item.name}><div className="flex justify-between gap-3 text-[10px]"><span className="flex items-center gap-2 min-w-0"><span className="w-2 h-2 rounded-sm shrink-0" style={{background:COLORS[index%COLORS.length]}}/><span className="truncate text-slate-300">{item.name}</span></span><span className="font-mono">{money(item.amount,true)}</span></div><div className="h-1 bg-white/[.05] rounded mt-1.5 overflow-hidden"><div className="h-full rounded" style={{width:`${expenseTotal ? item.amount/expenseTotal*100 : 0}%`,background:COLORS[index%COLORS.length]}}/></div></div>)}</div>
        </div>
      </Panel>
    </div>

    <div className="grid xl:grid-cols-[minmax(0,1.4fr)_minmax(300px,.6fr)] gap-4">
      <Panel title="Future scope" subtitle="Decision prompts generated from the current operating shape">
        <div className="grid md:grid-cols-3 gap-3">
          <Insight tone="cyan" title="Cash remains resilient" text={`Under this scenario, FY closing cash is ${money(forecastEnd, true)}. Test slower collections before committing to fixed costs.`}/>
          <Insight tone="violet" title="Protect contribution margin" text={`${biggestCost?.name || "Your largest cost"} is ${expenseTotal ? Math.round((biggestCost?.amount || 0)/expenseTotal*100) : 0}% of spend. Model vendor or hiring changes here first.`}/>
          <Insight tone="amber" title="Collection speed matters" text={`${assumptions.collectionDays} days of collection lag is applied. The cash curve updates instantly as this assumption changes.`}/>
        </div>
      </Panel>
      <Panel title="Next review" subtitle="A compact monthly operating rhythm">
        <div className="space-y-2">
          {["Reconcile Zoho against bank statements", "Review overdue customer collections", "Lock next 90-day operating plan"].map((item,index)=><div key={item} className="flex items-center gap-3 rounded-lg border border-white/[.07] p-3"><div className={`h-6 w-6 rounded-full grid place-items-center text-[10px] ${index===0?"bg-emerald-400/10 text-emerald-300":"bg-white/[.04] text-slate-400"}`}>{index===0?<Check size={13}/>:index+1}</div><span className="text-[11px] flex-1">{item}</span></div>)}
        </div>
      </Panel>
    </div>
  </div>;
}

function ScenarioControl({ label, value, suffix, min, max, onChange }) {
  return <label className="block"><div className="flex justify-between gap-2 text-[10px]"><span className="text-[var(--text-muted)]">{label}</span><span className="font-mono text-white">{value}{suffix}</span></div><input type="range" min={min} max={max} value={value} onChange={event=>onChange(Number(event.target.value))} className="w-full accent-cyan-400 mt-2"/></label>;
}

function Insight({ title, text, tone }) {
  const colors={cyan:"border-cyan-400/20 bg-cyan-400/[.05]",violet:"border-violet-400/20 bg-violet-400/[.05]",amber:"border-amber-400/20 bg-amber-400/[.05]"};
  return <article className={`rounded-lg border p-4 ${colors[tone]}`}><div className="text-xs font-semibold">{title}</div><p className="text-[10px] leading-4 text-[var(--text-secondary)] mt-2">{text}</p></article>;
}
